import {assertAndContinue} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {ActionMethod, ActionOrigin} from 'constants/AnalyticsConstants';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {readQuerySafe} from 'graphql-client/ApolloWrappers';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {
  bulkCreateOrUpdateProcessTasks,
  CreateOrUpdateServerProcessTask,
  EditExistingTask,
} from 'processes/dao/ProcessManagementDAO';
import {isServerRequestError} from 'util/Net';
import pick from 'util/pick';
import {withLoadingScreen} from 'util/Promises';

import {ProcessTaskGraphQL} from '../../../../graphql-fragments/processes/__generated/ProcessTaskGraphQL';
import {
  ProcessTaskDetailsQuery,
  ProcessTaskDetailsQueryVariables,
} from '../graphql/__generated/ProcessTaskDetailsQuery';
import {PROCESS_TASK_DETAILS_QUERY} from '../graphql/GetProcessTask';

type ProcessTaskGql = ExtractByTypename<ProcessTaskDetailsQuery['processTask'], 'ProcessTaskNode'>;

type UpdateCachedTask = Pick<
  ProcessTaskGql,
  'status' | 'fields' | 'assignee' | 'scheduledOnDate' | 'executionOrigin' | 'responsibleParties'
>;
export type UpdateCachedTaskParams = Partial<UpdateCachedTask> & {id: string};

export type UpdateTaskParams = Omit<
  EditExistingTask,
  'actionOrigin' | 'actionMethod' | 'sourceType' | 'creationOriginId'
>;

type UpdateTaskToCachedTaskType = Required<{
  [key in keyof UpdateTaskParams]: Array<keyof UpdateCachedTaskParams>;
}>;

export function updateProcessTaskDetailsQueryCache(updatedTask: UpdateCachedTaskParams): void {
  const response = readQuerySafe<ProcessTaskDetailsQuery, ProcessTaskDetailsQueryVariables>(
    {query: PROCESS_TASK_DETAILS_QUERY},
    {variables: {processTaskId: updatedTask.id}}
  );
  if (response == null) {
    return;
  }
  if (isInaccessible(response.processTask)) {
    assertAndContinue(false, `Cannot update inaccessible response, id: ${response.processTask.invalidId}`);
    return;
  }

  const taskToWrite: ProcessTaskGql = {
    ...response.processTask,
    ...updatedTask,
  };

  getApolloClient().writeQuery({
    query: PROCESS_TASK_DETAILS_QUERY,
    data: {
      processTask: taskToWrite,
    },
    variables: {processTaskId: updatedTask.id},
  });
}

async function updateProcessTask(
  {id, assigneeId, statusId, fields, scheduledOnDate, responsiblePartiesIds}: UpdateTaskParams,
  dataModsHeader?: DataModificationsHeader
): Promise<ProcessTaskGraphQL> {
  const updateTask: CreateOrUpdateServerProcessTask = {
    id,
    sourceType: 'task_details',
    actionOrigin: ActionOrigin.WORKFLOWS_TASK_DETAILS_PAGE,
    actionMethod: ActionMethod.BUTTON_CLICK,
    ...(fields ? {fields} : {}),
    ...(assigneeId !== undefined ? {assigneeId} : {}),
    ...(statusId != null ? {statusId} : {}),
    ...(scheduledOnDate !== undefined ? {scheduledOnDate} : {}),
    ...(responsiblePartiesIds !== undefined ? {responsiblePartiesIds} : {}),
  };
  const result = await withLoadingScreen(
    bulkCreateOrUpdateProcessTasks({tasks: [updateTask], dataModsHeader})
  );
  notifications.success('Task updated successfully.');
  return result.tasks[0];
}

function getValuesToUpdateInCache(updateTaskParams: UpdateTaskParams): Array<keyof ProcessTaskGraphQL> {
  // Mapping between what is updated via the API and what is written to the cache.
  const updatedPropertyToRelevantCachedProperties: UpdateTaskToCachedTaskType = {
    id: ['id'],
    assigneeId: ['assignee'],
    fields: ['fields'],
    statusId: ['status', 'executionOrigin'],
    scheduledOnDate: ['scheduledOnDate'],
    responsiblePartiesIds: ['responsibleParties'],
    // TODO(BNCH-74025): Once the details page actually renders studies, fetch studies and populate cache.
    studyIds: [],
  };

  const cachedKeys = Object.values(
    pick(
      updatedPropertyToRelevantCachedProperties,
      ...(Object.keys(updateTaskParams) as Array<keyof UpdateTaskParams>)
    )
  ).flat();
  return cachedKeys;
}

export async function updateProcessTaskAndCache(
  updateTaskParams: UpdateTaskParams,
  dataModsHeader?: DataModificationsHeader
): Promise<ProcessTaskGraphQL | null> {
  try {
    const updatedTask = await updateProcessTask(updateTaskParams, dataModsHeader);
    updateProcessTaskDetailsQueryCache(pick(updatedTask, ...getValuesToUpdateInCache(updateTaskParams)));
    return updatedTask;
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    if (isServerRequestError(e)) {
      notifications.notifyError(e);
    }
    return null;
  }
}
